import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, UserGroupIcon, CheckCircleIcon, XCircleIcon, PlayIcon } from '@heroicons/react/24/outline';
import RoomChat from './RoomChat';
import { toast } from 'react-hot-toast';
import websocketRoomClient from '../../utils/websocketRoomClient';

const RoomDetails = ({ room, userId, username, onLeaveRoom, onSendMessage, onSetReady, onPlay, viewOnly = false }) => {
  const [isReady, setIsReady] = useState(false);
  const navigate = useNavigate();

  // Creator check
  const isCreator = room?.createdBy?.userId === userId;

  // Handle joining game for non-creator players with useCallback for proper dependencies
  const handleJoinGame = useCallback(() => {
    // Check if we're already in game status
    if (room?.status === 'in_game') {
      console.log('Attempting to join game as non-creator');
      
      // Force a room GET_ROOMS refresh to get the latest game info
      if (websocketRoomClient.connected) {
        websocketRoomClient.refreshRooms();
      }
      
      // Determine player roles
      const readyPlayers = room.participants.filter(p => p.isReady);
      let challenger = null;
      let rival = null;
      
      if (readyPlayers.length >= 2) {
        // Use the first two ready players as challenger and rival
        challenger = readyPlayers[0].userId;
        rival = readyPlayers[1].userId;
      } else if (room.participants.length >= 2) {
        // Fallback to using the first two participants
        challenger = room.participants[0].userId;
        rival = room.participants[1].userId;
      }
      
      // Determine user's role
      let role = null;
      if (userId === challenger) {
        role = 'challenger';
      } else if (userId === rival) {
        role = 'rival';
      } else {
        // If user is not one of the first two players, assign a role based on position
        role = room.participants.findIndex(p => p.userId === userId) % 2 === 0 ? 'challenger' : 'rival';
      }
      
      // Manually construct game URL
      setTimeout(() => {
        try {
          const protocol = window.location.protocol;
          const hostname = window.location.hostname;
          const port = '3001'; // Hardcoded game port
          const currentTime = Date.now();
          
          // Build URL with all necessary parameters
          let gameUrl = `${protocol}//${hostname}:${port}/?roomID=${room.roomId}&ts=${currentTime}`;
          
          // Add match ID if available
          if (room.matches && room.matches.length > 0) {
            const latestMatch = room.matches[room.matches.length - 1];
            if (latestMatch.matchId) {
              gameUrl += `&poolID=${latestMatch.matchId}`;
            }
          }
          
          // Add user ID
          gameUrl += `&userID=${userId}`;
          
          // Add challenger and rival info
          if (challenger) gameUrl += `&challenger=${challenger}`;
          if (rival) gameUrl += `&rival=${rival}`;
          
          // Add explicit role
          if (role) gameUrl += `&role=${role}`;
          
          console.log('Non-creator joining game with URL:', gameUrl);
          console.log('Player roles:', { challenger, rival, userRole: role });
          
          // Show notification
          toast.success('Game started by room creator. Joining game...', {
            duration: 3000
          });
          
          // Navigate to game after a short delay
          setTimeout(() => {
            window.location.href = gameUrl;
          }, 1500);
        } catch (err) {
          console.error('Failed to join game:', err);
          toast.error('Failed to join game. Please try again.');
        }
      }, 2000);
    }
  }, [room, userId]); // Dependencies for the callback
  
  // Single useEffect for status updates and auto-joining
  useEffect(() => {
    // Find the current user in participants and update ready status
    const participant = room?.participants?.find(p => p.userId === userId);
    if (participant) {
      setIsReady(participant.isReady);
    }

    // If room status changes to in_game, handle game URL for non-creator
    if (room?.status === 'in_game' && !isCreator) {
      // For non-creator players, if room status is in_game, try to join
      handleJoinGame();
    }
  }, [room, userId, isCreator, handleJoinGame]);

  if (!room) {
    return (
      <div className="bg-dark-lighter rounded-lg p-8 text-center">
        <p className="text-gray-400 mb-4">Room not found or loading...</p>
        <button 
          onClick={() => navigate('/duel')}
          className="bg-primary hover:bg-primary/90 text-white px-4 py-2 rounded-lg font-medium"
        >
          Back to Rooms
        </button>
      </div>
    );
  }

  const { roomId, name, createdBy, participants, maxParticipants, status, chat } = room;
  
  const handleToggleReady = () => {
    const newStatus = !isReady;
    setIsReady(newStatus);
    onSetReady(roomId, newStatus);
  };

  const handlePlay = () => {
    console.log('Room creator starting game');
    
    // Before calling onPlay, create a direct listener for game launch
    if (websocketRoomClient.connected) {
      websocketRoomClient.on('gameLaunch', function gameStartHandler(data) {
        console.log('Creator received game launch event:', data);
        
        // Remove this one-time listener
        websocketRoomClient.off('gameLaunch', gameStartHandler);
        
        try {
          // Extract game URL from the event
          let gameUrl = data.url;
          
          // Make sure the URL contains all necessary parameters for multiplayer
          const url = new URL(gameUrl, window.location.origin);
          
          // Add userID - critical for multiplayer
          if (!url.searchParams.has('userID')) {
            url.searchParams.set('userID', userId);
          }
          
          // Add role parameter
          if (!url.searchParams.has('role')) {
            url.searchParams.set('role', 'challenger');
          }
          
          // Add challenger parameter (creator is always challenger)
          if (!url.searchParams.has('challenger')) {
            url.searchParams.set('challenger', userId);
          }
          
          // Ensure these are carried over from original URL
          if (data.matchId && !url.searchParams.has('poolID')) {
            url.searchParams.set('poolID', data.matchId);
          }
          
          if (data.roomId && !url.searchParams.has('roomID')) {
            url.searchParams.set('roomID', data.roomId);
          }
          
          // Update the local URL for use
          gameUrl = url.toString();
          
          // Add a random timestamp for cache busting
          if (gameUrl.includes('?')) {
            gameUrl += `&_t=${Date.now()}`;
          } else {
            gameUrl += `?_t=${Date.now()}`;
          }
          
          console.log('Creator launching enhanced game URL:', gameUrl);
          
          // Show notification
          toast.success('Starting game...', {
            duration: 1500
          });
          
          // Redirect after a short delay
          setTimeout(() => {
            window.location.href = gameUrl;
          }, 1000);
        } catch (err) {
          console.error('Failed to process game URL:', err);
          toast.error('Failed to start game. Please try again.');
        }
      });
    }
    
    // Call the original onPlay
    onPlay(roomId);
  };

  // Count ready players
  const readyPlayersCount = participants.filter(p => p.isReady).length;
  
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6">
        {/* Room Header */}
        <div className="bg-dark-lighter rounded-lg p-6 border border-gray-700">
          <div className="flex items-center gap-4 mb-4">
            <button 
              onClick={() => navigate('/duel')}
              className="p-2 rounded-full bg-gray-700 hover:bg-gray-600 text-white"
            >
              <ArrowLeftIcon className="h-5 w-5" />
            </button>
            <div>
              <h2 className="text-xl font-bold text-white">{name}</h2>
              <p className="text-gray-400 text-sm">
                Created by {createdBy.username} {isCreator && ' (You)'}
              </p>
            </div>
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <UserGroupIcon className="h-5 w-5 text-blue-400" />
              <span className="text-gray-300">
                {participants.length}/{maxParticipants} Players
              </span>
            </div>
            
            {viewOnly ? (
              <div className="flex gap-3">
                <button
                  onClick={() => onLeaveRoom(roomId)}
                  className="px-4 py-2 rounded-lg bg-gray-600 hover:bg-gray-700 text-white"
                >
                  Back to Rooms
                </button>
              </div>
            ) : (
              <div className="flex gap-3">
                <button
                  onClick={handleToggleReady}
                  className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
                    isReady 
                      ? 'bg-green-600 hover:bg-green-700 text-white' 
                      : 'bg-gray-700 hover:bg-gray-600 text-white'
                  }`}
                >
                  {isReady ? (
                    <>
                      <CheckCircleIcon className="h-5 w-5" />
                      Ready
                    </>
                  ) : (
                    <>
                      <XCircleIcon className="h-5 w-5" />
                      Not Ready
                    </>
                  )}
                </button>
                
                {isCreator ? (
                  <button
                    onClick={handlePlay}
                    disabled={readyPlayersCount < 2 || status === 'in_game'}
                    className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
                      readyPlayersCount >= 2 && status !== 'in_game'
                        ? 'bg-primary hover:bg-primary/90 text-white' 
                        : 'bg-gray-700 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    <PlayIcon className="h-5 w-5" />
                    Play
                  </button>
                ) : (
                  <button
                    onClick={handleJoinGame}
                    disabled={status !== 'in_game'}
                    className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
                      status === 'in_game'
                        ? 'bg-primary hover:bg-primary/90 text-white' 
                        : 'bg-gray-700 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    <PlayIcon className="h-5 w-5" />
                    Join Game
                  </button>
                )}
                
                <button
                  onClick={() => onLeaveRoom(roomId)}
                  className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white"
                >
                  Leave Room
                </button>
              </div>
            )}
          </div>
          
          {status === 'in_game' && (
            <div className="mt-4 p-3 bg-green-500/20 text-green-400 rounded-lg text-center">
              Game is in progress! {!isCreator && "Click 'Join Game' to enter."}
            </div>
          )}
        </div>
        
        {/* Participants */}
        <div className="bg-dark-lighter rounded-lg p-6 border border-gray-700">
          <h3 className="text-lg font-semibold text-white mb-4">Room Participants</h3>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {participants.map((participant) => (
              <div 
                key={participant.userId} 
                className="bg-dark-lightest rounded-lg p-4 border border-gray-700 flex justify-between items-center"
              >
                <div>
                  <p className="text-white font-medium">
                    {participant.username} 
                    {participant.userId === userId && ' (You)'}
                    {participant.userId === createdBy.userId && ' 👑'}
                  </p>
                  <p className="text-xs text-gray-400">
                    Joined: {new Date(participant.joinedAt).toLocaleString()}
                  </p>
                </div>
                
                <div className={`w-3 h-3 rounded-full ${
                  participant.isReady ? 'bg-green-500' : 'bg-gray-500'
                }`}></div>
              </div>
            ))}
            
            {/* Empty slots */}
            {Array.from({ length: maxParticipants - participants.length }).map((_, index) => (
              <div 
                key={`empty-${index}`} 
                className="bg-dark-lightest rounded-lg p-4 border border-gray-700 flex justify-center items-center text-gray-500"
              >
                Empty Slot
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* Chat Section */}
      <div className="lg:col-span-1">
        {viewOnly ? (
          <div className="bg-dark-lighter rounded-lg p-6 border border-gray-700 h-full">
            <h3 className="text-lg font-semibold text-white mb-4">Chat Log</h3>
            <div className="overflow-y-auto h-96 mb-4">
              {(chat || []).length > 0 ? (
                (chat || []).map((message, index) => (
                  <div key={index} className="mb-3">
                    <div className="text-sm">
                      <span className="font-semibold text-blue-400">{message.username || 'Unknown'}: </span>
                      <span className="text-gray-300">{message.message}</span>
                    </div>
                    <div className="text-xs text-gray-500">
                      {new Date(message.timestamp).toLocaleString()}
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-400 text-center py-4">No messages yet</p>
              )}
            </div>
            <div className="p-3 bg-yellow-600/20 border border-yellow-600/50 rounded-lg">
              <p className="text-yellow-300 text-center text-sm">
                Connect your wallet to send messages
              </p>
            </div>
          </div>
        ) : (
          <RoomChat
            roomId={roomId}
            userId={userId}
            username={username}
            messages={chat || []}
            onSendMessage={onSendMessage}
          />
        )}
      </div>
    </div>
  );
};

export default RoomDetails;
